<template>
    <TkRightDrawer
        :model-value="modelValue"
        :title="isEditing ? 'Edit Enquiry' : 'Create Enquiry'"
        :subtitle="isEditing ? 'Update the enquiry details.' : 'Record a new enquiry.'"
        width="720px"
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <q-form ref="form" @submit.prevent="submit">
            <q-card-section>
                <div class="row q-col-gutter-md">
                    <div class="col-12 col-md-6 tk-field">
                        <p class="q-mb-xs"><strong>Enquiry Date</strong></p>
                        <TkDateField v-model="form.enquiryDate" :rules="dateRules" />
                    </div>
                    <div class="col-12 col-md-6 tk-field">
                        <p class="q-mb-xs"><strong>Enquirer Name</strong></p>
                        <q-input
                            v-model="form.enquirerName"
                            outlined
                            dense
                            maxlength="255"
                            lazy-rules="ondemand"
                            hide-bottom-space
                            :rules="[requiredRule('Enquirer Name')]"
                        />
                    </div>
                    <div class="col-12 col-md-6 tk-field">
                        <p class="q-mb-xs"><strong>Method</strong></p>
                        <q-select
                            v-model="form.methodOptionId"
                            :options="methodOptions"
                            outlined
                            dense
                            emit-value
                            map-options
                            lazy-rules="ondemand"
                            hide-bottom-space
                            :rules="[requiredRule('Method')]"
                        />
                    </div>
                    <div class="col-12 col-md-6 tk-field">
                        <p class="q-mb-xs"><strong>Type</strong></p>
                        <q-select
                            v-model="form.typeOptionId"
                            :options="typeOptions"
                            outlined
                            dense
                            emit-value
                            map-options
                            lazy-rules="ondemand"
                            hide-bottom-space
                            :rules="[requiredRule('Type')]"
                        />
                    </div>
                    <div class="col-12 col-md-6 tk-field">
                        <p class="q-mb-xs"><strong>Staff Member</strong></p>
                        <q-select
                            v-model="form.relatedStaffId"
                            :options="staffOptions"
                            outlined
                            dense
                            emit-value
                            map-options
                            lazy-rules="ondemand"
                            hide-bottom-space
                            :rules="[requiredRule('Staff Member')]"
                        />
                    </div>
                    <div class="col-12 col-md-6 tk-field">
                        <p class="q-mb-xs"><strong>Service</strong></p>
                        <q-select
                            v-model="form.serviceCode"
                            :options="serviceOptions"
                            outlined
                            dense
                            clearable
                            emit-value
                            map-options
                        />
                    </div>
                    <div class="col-12 col-md-6 tk-field">
                        <p class="q-mb-xs"><strong>Time Taken (minutes)</strong></p>
                        <q-input
                            v-model="form.timeSpentMinutes"
                            outlined
                            dense
                            type="number"
                            min="0"
                            max="1440"
                            step="1"
                            lazy-rules="ondemand"
                            hide-bottom-space
                            :rules="[minutesRule]"
                        />
                    </div>
                    <div class="col-12 tk-field">
                        <p class="q-mb-xs"><strong>Notes</strong></p>
                        <q-input v-model="form.notes" outlined dense type="textarea" rows="8" />
                    </div>
                </div>
            </q-card-section>
            <q-card-actions align="right">
                <q-btn flat no-caps color="negative" label="Cancel" @click="$emit('update:modelValue', false)" />
                <q-btn
                    unelevated
                    no-caps
                    color="primary"
                    icon="save"
                    :label="isEditing ? 'Save Enquiry' : 'Create Enquiry'"
                    type="submit"
                    :loading="saving"
                />
            </q-card-actions>
        </q-form>
    </TkRightDrawer>
</template>

<script>
import TkDateField from "@/components/TkClients/TkDateField.vue";
import TkRightDrawer from "@/components/TkClients/TkRightDrawer.vue";
import { toEnquiryDateOnly, toEnquiryFormDate } from "@/services/util/enquiries";

const buildForm = (enquiry, defaultStaffId) => ({
    enquiryDate: toEnquiryFormDate(enquiry?.enquiry_date || new Date()),
    enquirerName: enquiry?.enquirer_name || "",
    methodOptionId: enquiry?.method_option_id || null,
    typeOptionId: enquiry?.type_option_id || null,
    relatedStaffId: enquiry?.related_staff_id || defaultStaffId,
    serviceCode: enquiry?.service_code || null,
    timeSpentMinutes: enquiry?.time_spent_minutes ?? null,
    notes: enquiry?.notes || "",
});

export default {
    name: "TkEnquiryDrawer",
    components: { TkDateField, TkRightDrawer },
    emits: ["save", "update:modelValue"],
    props: {
        modelValue: {
            type: Boolean,
            required: true,
        },
        enquiry: {
            type: Object,
            required: false,
            default: null,
        },
        defaultStaffId: {
            type: Number,
            required: false,
            default: null,
        },
        methodOptions: {
            type: Array,
            required: true,
        },
        typeOptions: {
            type: Array,
            required: true,
        },
        staffOptions: {
            type: Array,
            required: true,
        },
        serviceOptions: {
            type: Array,
            required: true,
        },
        saving: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            form: buildForm(null, this.defaultStaffId),
        };
    },
    computed: {
        isEditing() {
            return !!this.enquiry?.id;
        },
        dateRules() {
            return [
                this.requiredRule("Enquiry Date"),
                value => toEnquiryDateOnly(value) !== null || "Enter a valid date.",
            ];
        },
    },
    watch: {
        modelValue(value) {
            if (value) this.resetForm();
        },
        enquiry() {
            if (this.modelValue) this.resetForm();
        },
    },
    methods: {
        requiredRule(field) {
            return value => !!String(value ?? "").trim() || `${field} is required.`;
        },
        minutesRule(value) {
            if (value == null || value === "") return true;
            const minutes = Number(value);
            return Number.isInteger(minutes) && minutes >= 0 && minutes <= 1440
                || "Time Taken must be a whole number from 0 to 1440.";
        },
        resetForm() {
            this.form = buildForm(this.enquiry, this.defaultStaffId);
            this.$nextTick(() => this.$refs.form?.resetValidation());
        },
        async submit() {
            if (!await this.$refs.form?.validate()) return;
            this.$emit("save", {
                method_option_id: this.form.methodOptionId,
                type_option_id: this.form.typeOptionId,
                enquiry_date: toEnquiryDateOnly(this.form.enquiryDate),
                enquirer_name: this.form.enquirerName.trim(),
                related_staff_id: this.form.relatedStaffId,
                service_code: this.form.serviceCode || null,
                time_spent_minutes: this.form.timeSpentMinutes == null || this.form.timeSpentMinutes === ""
                    ? null
                    : Number(this.form.timeSpentMinutes),
                notes: this.form.notes.trim() || null,
            });
        },
    },
};
</script>
